/**
 * arrivalContact.ts — 貸出予約の「連絡」（到着連絡 / 空港）の読み出し（2026-09-19）
 *
 * 貸出行の「連絡」ボタンと車両ポジションのピックアップサブゾーンは、予約の json_data に
 *   到着連絡: 有り
 *   空港:     未 | ロビー | 14番(宮古: 乗降場) | 送迎車 | 返却車 | 直接来店
 * で保存されている（page-daylist.md「ピックアップエリア内サブゾーン」）。
 * 値の意味づけ（どれが対応中か・どれで運転者が要るか・どれが店舗で出発準備か）を
 * ここに1つだけ置き、リストと車両ポジションの両方が参照する。
 */
import { extractJsonDataRecord } from './hotelDelivery';

export const ARRIVAL_CONTACT_KEY = '到着連絡';
export const AIRPORT_KEY = '空港';
export const ARRIVAL_CONTACT_ON = '有り';

/**
 * こちらから電話連絡した印（車両ポジションの予約リスト「連絡済み」サブエリア。2026-10-19 に「空港」へ統合）。
 * 「空港: 連絡済」だけを保存し「到着連絡」は入れない（お客様からの到着連絡とは区別したまま）。
 * 以前は別キー「電話連絡: 済」だった。過去分は移し替えない（2026-10-19 指示）。
 */
export const AIRPORT_CALLED = '連絡済';
/** 「連絡済」の表示色（リストの連絡ボタン・チップ） */
export const CALLED_COLOR = '#546e7a';

/** 到着連絡はまだで、こちらから電話連絡済み（空港=連絡済）か */
export function isCalledOnly(reservation: { json_data?: unknown; detail?: { json_data?: unknown } | null } | null | undefined): boolean {
  const data = extractJsonDataRecord(reservation?.json_data ?? reservation?.detail?.json_data ?? null);
  if (!data || data[ARRIVAL_CONTACT_KEY] === ARRIVAL_CONTACT_ON) return false;
  return String(data[AIRPORT_KEY] ?? '').trim() === AIRPORT_CALLED;
}

/** お客様が自分で店舗に来る。スタッフは迎えに動かない */
export const AIRPORT_WALKIN = '直接来店';
/**
 * 送迎車 / 返却車 でお客様を店舗まで運ぶ。アサイン車両は店舗で待機し、
 * 送迎の運転者が要る
 */
export const SHUTTLE_AIRPORT_VALUES: ReadonlySet<string> = new Set(['送迎車', '返却車']);
/**
 * 店舗ゾーンの「出発準備」に並ぶ値。アサイン車両が店舗に居たまま出発を待つ受け方
 * （送迎車 / 返却車 / 直接来店）。ロビー・14番・連絡あり(未) は車が迎えに出るので含めない
 */
export const PREPARE_AIRPORT_VALUES: ReadonlySet<string> = new Set(['送迎車', '返却車', AIRPORT_WALKIN]);

export interface ArrivalContact {
  /** 到着連絡が ON か */
  on: boolean;
  /** 空港サブゾーンの値。ON でも未設定なら '未' */
  airport: string;
}

/** 予約（json_data または detail.json_data）から到着連絡を読む */
export function getArrivalContact(reservation: { json_data?: unknown; detail?: { json_data?: unknown } | null } | null | undefined): ArrivalContact {
  const raw = reservation?.json_data ?? reservation?.detail?.json_data ?? null;
  const data = extractJsonDataRecord(raw);
  const on = data?.[ARRIVAL_CONTACT_KEY] === ARRIVAL_CONTACT_ON;
  const airport = on ? String(data?.[AIRPORT_KEY] ?? '未').trim() || '未' : '';
  return { on, airport };
}

/**
 * 「連絡」が対応中を表すか。到着連絡 ON で、直接来店以外
 * （お客様が来るのを待つだけの直接来店は、誰も動いていないので対応中ではない）
 */
export const isArrivalContactInProgress = (c: ArrivalContact): boolean => c.on && c.airport !== AIRPORT_WALKIN;

/** 送迎の運転者が要る受け方か（送迎車 / 返却車） */
export const needsShuttleDriver = (c: ArrivalContact): boolean => c.on && SHUTTLE_AIRPORT_VALUES.has(c.airport);

/** アサイン車両が店舗で出発準備に入る受け方か（送迎車 / 返却車 / 直接来店） */
export const isPreparingAtShop = (c: ArrivalContact): boolean => c.on && PREPARE_AIRPORT_VALUES.has(c.airport);

/**
 * 到着連絡のリクエスト（RequestWindow の line_arrival）を「実行」すると、すでに配置済みの予約を
 * 同じ所に置き直すだけになるか（2026-11-01）。
 * - 到着連絡 ON で、同じサブゾーンにいる → 配置済み
 * - 到着連絡 ON で、新しい連絡のゾーンが「未（連絡あり）」→ 配置済み。
 *   ロビー・14番などに進んだ予約を「連絡あり」へ戻してしまうので、置き直さない
 * - それ以外（未配置・別の具体的なゾーンへ移る連絡）→ 配置済みではない（従来どおり実行を出す）
 */
export function isAlreadyPlacedForArrival(current: ArrivalContact, zone: string): boolean {
  if (!current.on) return false;
  const z = String(zone || '').trim() || '未';
  return z === '未' || current.airport === z;
}
